import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const PayrollQuestionManager = ({ onBack }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [adminResponse, setAdminResponse] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filter, setFilter] = useState('submitted'); // submitted, answered, resolved, dismissed

  useEffect(() => {
    fetchQuestions();
  }, [filter]);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      console.log('🔄 Fetching questions...');
      const { data, error } = await supabase
        .from('payroll_time_question')
        .select('*')
        .order('submitted_at', { ascending: false });

      if (error) throw error;
      console.log('📊 Raw questions data:', data);
      
      // Group conversations by payroll_number + employee_number
      const conversations = {};
      (data || []).forEach(record => {
        const key = `${record.payroll_number}_${record.employee_number}`;
        if (!conversations[key]) {
          conversations[key] = {
            payroll_number: record.payroll_number,
            employee_number: record.employee_number,
            messages: [],
            latestStatus: 'submitted',
            latestTimestamp: record.submitted_at
          };
        }
        conversations[key].messages.push(record);
      });
      
      // Determine conversation status based on the original question's status
      Object.values(conversations).forEach(conv => {
        // Sort messages by date
        conv.messages.sort((a, b) => new Date(a.submitted_at) - new Date(b.submitted_at));
        
        // Find the latest employee question (not admin response)
        const latestEmployeeQuestion = conv.messages
          .filter(m => !m.question.startsWith('[ADMIN RESPONSE]'))
          .sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at))[0];
        
        // Use the status of the latest employee question as the conversation status
        if (latestEmployeeQuestion) {
          conv.latestStatus = latestEmployeeQuestion.status;
          conv.latestTimestamp = latestEmployeeQuestion.updated_at || latestEmployeeQuestion.submitted_at;
          console.log(`📌 Conversation ${conv.payroll_number}_${conv.employee_number} status:`, {
            questionId: latestEmployeeQuestion.id,
            status: latestEmployeeQuestion.status,
            updatedAt: latestEmployeeQuestion.updated_at
          });
        }
        
        // Get the most recent activity timestamp
        const mostRecentMessage = conv.messages
          .sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at))[0];
        if (mostRecentMessage) {
          conv.latestActivityTimestamp = mostRecentMessage.submitted_at;
        }
      });
      
      // Convert to array and filter by status
      const conversationList = Object.values(conversations)
        .filter(conv => {
          if (filter === 'submitted') return conv.latestStatus === 'submitted';
          if (filter === 'answered') return conv.latestStatus === 'answered';
          if (filter === 'resolved') return conv.latestStatus === 'resolved';
          if (filter === 'dismissed') return conv.latestStatus === 'dismissed';
          return true;
        })
        .sort((a, b) => new Date(b.latestActivityTimestamp) - new Date(a.latestActivityTimestamp));
      
      setQuestions(conversationList);
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (conversation, newStatus, response = '') => {
    setIsSubmitting(true);
    try {
      if (response && response.trim()) {
        // Create a new record for admin response
        const newRecord = {
          payroll_number: conversation.payroll_number,
          employee_number: conversation.employee_number,
          question: `[ADMIN RESPONSE] ${response.trim()}`,
          status: 'answered',
          admin_response: response.trim(),
          submitted_at: new Date().toISOString(),
          answered_at: new Date().toISOString(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        // Generate question number for admin response
        const { data: questionCount } = await supabase
          .from('payroll_time_question')
          .select('question_no')
          .order('question_no', { ascending: false })
          .limit(1);

        if (questionCount && questionCount.length > 0) {
          const lastQuestionNo = questionCount[0].question_no;
          const lastNumber = parseInt(lastQuestionNo.replace('Q', ''));
          newRecord.question_no = `Q${String(lastNumber + 1).padStart(6, '0')}`;
        } else {
          newRecord.question_no = 'Q000001';
        }

        const { data: newQuestionData, error: insertError } = await supabase
          .from('payroll_time_question')
          .insert(newRecord)
          .select()
          .single();

        if (insertError) throw insertError;

        // Send SMS notification to employee about the response
        await sendSMSToEmployee(newQuestionData, 'answered', response);
        
        console.log('✅ SMS sent for admin response to:', conversation.employee_number);
      }

      // Update status of latest employee question if needed
      if (newStatus === 'resolved' || newStatus === 'dismissed') {
        console.log('🔄 Updating status to:', newStatus);
        const latestEmployeeQuestion = conversation.messages
          .filter(m => !m.question.startsWith('[ADMIN RESPONSE]'))
          .sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at))[0];

        console.log('📝 Latest employee question:', latestEmployeeQuestion);

        if (latestEmployeeQuestion) {
          const updates = {
            status: newStatus,
            updated_at: new Date().toISOString()
          };

          if (newStatus === 'resolved') {
            updates.resolved_at = new Date().toISOString();
          }

          console.log('📤 Updating question with:', updates);
          const { error: updateError } = await supabase
            .from('payroll_time_question')
            .update(updates)
            .eq('id', latestEmployeeQuestion.id);

          if (updateError) {
            console.error('❌ Error updating status:', updateError);
            throw updateError;
          }

          console.log('✅ Status updated successfully');

          // Send SMS notification for status change
          await sendSMSToEmployee(latestEmployeeQuestion, newStatus, '');
          console.log('✅ SMS notification sent for status change');
        }
      }

      // Refresh the questions list
      await fetchQuestions();
      setSelectedQuestion(null);
      setAdminResponse('');

      // Show success message
      if (newStatus === 'resolved') {
        alert('Question marked as resolved successfully!');
      } else if (newStatus === 'dismissed') {
        alert('Question dismissed successfully!');
      } else if (response) {
        alert('Response sent successfully!');
      }

    } catch (error) {
      console.error('Error handling response:', error);
      alert('Error: ' + (error.message || 'Failed to update. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const sendSMSToEmployee = async (question, status, response) => {
    console.log(`📱 SMS to employee ${question.employee_number}:`, {
      questionNo: question.question_no,
      status,
      response
    });
    
    try {
      // Get employee phone number
      console.log('🔍 Looking up employee phone number...');
      const { data: employee, error: empError } = await supabase
        .from('employees')
        .select('phone, frappe_name')
        .eq('employee_number', question.employee_number)
        .single();
        
      console.log('👤 Employee lookup result:', { employee, empError });
        
      if (empError || !employee?.phone) {
        console.warn('❌ Employee phone not found for SMS:', question.employee_number, empError);
        return;
      }
      
      // Create SMS message based on status
      let message = '';
      const firstName = employee.frappe_name ? employee.frappe_name.split(' ')[0] : 'Hi';
      
      switch (status) {
        case 'answered':
          message = `Hi ${firstName}! Your payroll question #${question.question_no} has been answered: "${response}". Check https://pay.starside.app for details. Thank you!`;
          break;
        case 'resolved':
          message = `Hi ${firstName}! Your payroll question #${question.question_no} has been resolved. Check https://pay.starside.app for details. Thank you!`;
          break;
        case 'dismissed':
          message = `Hi ${firstName}! Your payroll question #${question.question_no} has been reviewed and dismissed. Contact admin if you need clarification. Thank you!`;
          break;
        default:
          message = `Hi ${firstName}! Your payroll question #${question.question_no} status has been updated. Check https://pay.starside.app for details. Thank you!`;
      }
      
      console.log('📝 SMS payload being sent:', {
        employee_name: employee.frappe_name,
        employee_number: question.employee_number,
        phone: employee.phone,
        message: message,
        messageLength: message.length
      });
      
      // Send SMS using existing working SMS API
      console.log('📤 Sending SMS request to API...');
      const smsResponse = await fetch('https://paystub.roborx.workers.dev/api/send-sms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipients: [{
            employee_name: employee.frappe_name,
            employee_number: question.employee_number,
            phone: employee.phone,
            message: message
          }],
          sendAll: false,
          skipPayrollValidation: true // Skip validation for question notifications
        })
      });
      
      console.log('📊 SMS API Response status:', smsResponse.status);
      console.log('📊 SMS API Response headers:', Object.fromEntries(smsResponse.headers.entries()));
      
      if (smsResponse.ok) {
        const result = await smsResponse.json();
        console.log('✅ SMS sent to employee successfully:', result);
        console.log('📊 SMS Results breakdown:', result.results);
      } else {
        const errorText = await smsResponse.text();
        console.warn('⚠️ SMS API error:', smsResponse.status, errorText);
      }
      
    } catch (error) {
      console.error('⚠️ SMS service error:', error.message);
      console.error('⚠️ SMS service error stack:', error.stack);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted': return 'bg-yellow-100 text-yellow-800';
      case 'answered': return 'bg-blue-100 text-blue-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'dismissed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Payroll Questions</h1>
          <p className="mt-2 text-gray-600">Manage employee payroll questions and disputes</p>
        </div>
        <button
          onClick={onBack}
          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
        >
          ← Back to Dashboard
        </button>
      </div>

      {/* Status Filter */}
      <div className="mb-6 flex space-x-2">
        {['submitted', 'answered', 'resolved', 'dismissed'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-lg font-medium ${
              filter === status
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-8">Loading questions...</div>
      ) : questions.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No {filter} questions found.
        </div>
      ) : (
        <div className="space-y-4">
          {questions.map((conversation) => (
            <div
              key={`${conversation.payroll_number}_${conversation.employee_number}`}
              className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className="font-semibold text-lg">
                      Conversation: {conversation.employee_number} - Payroll {conversation.payroll_number}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(conversation.latestStatus)}`}>
                      {conversation.latestStatus}
                    </span>
                    <span className="text-sm text-gray-500">
                      {conversation.messages.length} message{conversation.messages.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  
                  <div className="text-sm text-gray-600 mb-4">
                    <strong>Latest Activity:</strong> {formatDate(conversation.latestActivityTimestamp || conversation.latestTimestamp)}
                  </div>

                  {/* Conversation Thread */}
                  <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
                    {conversation.messages
                      .sort((a, b) => new Date(a.submitted_at) - new Date(b.submitted_at))
                      .map((message, index) => (
                      <div key={message.id} className={`p-3 rounded border ${
                        message.question.startsWith('[ADMIN RESPONSE]') 
                          ? 'bg-blue-50 border-blue-200 ml-8' 
                          : 'bg-gray-50 border-gray-200 mr-8'
                      }`}>
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-sm font-medium">
                            {message.question.startsWith('[ADMIN RESPONSE]') ? 'Admin' : message.employee_number}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDate(message.submitted_at)}
                          </span>
                        </div>
                        <p className="text-gray-900">
                          {message.question.startsWith('[ADMIN RESPONSE]') 
                            ? message.question.replace('[ADMIN RESPONSE] ', '')
                            : message.question
                          }
                        </p>
                        {message.question_no && (
                          <p className="text-xs text-gray-400 mt-1">#{message.question_no}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-2">
                <button
                  onClick={() => setSelectedQuestion(conversation)}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Add Response
                </button>
                
                {conversation.latestStatus === 'submitted' && (
                  <button
                    onClick={() => handleStatusChange(conversation, 'dismissed')}
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50"
                  >
                    Dismiss
                  </button>
                )}

                {(conversation.latestStatus === 'answered' || conversation.latestStatus === 'submitted') && (
                  <button
                    onClick={() => handleStatusChange(conversation, 'resolved')}
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    Mark Resolved
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Response Modal */}
      {selectedQuestion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-screen overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              Add Response to Conversation
            </h3>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-4">
                Employee: {selectedQuestion.employee_number} | Payroll: {selectedQuestion.payroll_number}
              </p>
              
              {/* Show recent conversation */}
              <div className="bg-gray-50 p-3 rounded border mb-4 max-h-32 overflow-y-auto">
                <strong>Recent Messages:</strong>
                <div className="mt-2 space-y-2">
                  {selectedQuestion.messages
                    .sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at))
                    .slice(0, 3)
                    .map(msg => (
                    <div key={msg.id} className="text-sm">
                      <span className="font-medium">
                        {msg.question.startsWith('[ADMIN RESPONSE]') ? 'Admin' : selectedQuestion.employee_number}:
                      </span>
                      <span className="ml-2">
                        {msg.question.startsWith('[ADMIN RESPONSE]') 
                          ? msg.question.replace('[ADMIN RESPONSE] ', '')
                          : msg.question.substring(0, 100) + (msg.question.length > 100 ? '...' : '')
                        }
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admin Response:
              </label>
              <textarea
                value={adminResponse}
                onChange={(e) => setAdminResponse(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg resize-none h-32 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Type your response to the employee..."
                disabled={isSubmitting}
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setSelectedQuestion(null);
                  setAdminResponse('');
                }}
                disabled={isSubmitting}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              
              <button
                onClick={() => handleStatusChange(
                  selectedQuestion, 
                  'answered', 
                  adminResponse
                )}
                disabled={isSubmitting || !adminResponse.trim()}
                className={`px-4 py-2 rounded text-white font-medium ${
                  isSubmitting || !adminResponse.trim()
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500'
                }`}
              >
                {isSubmitting ? 'Sending...' : 'Send Response & SMS'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollQuestionManager;